import { type DistanceBearingPitch, IkeUnitAngle, type Point } from '@ikegps/core-api';
import { CollectionModelFieldAnchor, CollectionModelFieldVector, IkeGeodesic, IkeUnit } from '@ikegps/core-shared';
import { addStyles, rgba } from '../../../components/theme';
import {
	BLACK,
	ColorPaletteBlue,
	ColorPaletteGrey,
	ColorPaletteOrange,
	ColorPaletteYellow,
	WHITE,
} from '../../../components/theme.palette';
import { m } from '../../../mithril';
import { Services } from '../../../services';
import { classNames } from '../../../util/classnames';
import { PointCache } from '../../google/projection.cache';
import type { PointCollectionLineInfo } from '../point.collection.cache';

interface ITopologicalEditorMapLineOptions {
	initialPoint: google.maps.Point;
	vector: PointCollectionLineInfo;
	isSelected: boolean;
	onClick: () => void;
	onVectorHandleMouseDown: (event: MouseEvent) => void;
	onVectorMouseOver?: () => void;
	onVectorMouseOut?: () => void;
	vectorHandleMoveDelta: Point;
	showVectorLabel: boolean;
	drawLabels: boolean;
}

export type SpanType = 'span' | 'dropspan' | 'vector-anchor' | 'anchor';

export type MaybeSpanType = SpanType | 'unknown';

const LineCSS = {
	container: 'c-TopologicalEditorMapLine',
	vector: 'c-TopologicalEditorMapLine__vector',
	line: 'c-TopologicalEditorMapLine__line',
	highlight: 'c-TopologicalEditorMapLine__highlight',
	lineSelected: 'c-TopologicalEditorMapLine__line--selected',
	label: 'c-TopologicalEditorMapLine__label',
	labelText: 'c-TopologicalEditorMapLine__labelText',
	clickTarget: 'c-TopologicalEditorMapLine__clickTarget',
	handle: 'c-TopologicalEditorMapLine__handle',
	dragging: 'c-TopologicalEditorMapLine--dragging',
	labelSpan: `c-TopologicalEditorMapLine__label--span`,
	labelDropspan: `c-TopologicalEditorMapLine__label--dropSpan`,
	labelVectorAnchor: `c-TopologicalEditorMapLine__label--vectorAnchor`,
	labelAnchor: `c-TopologicalEditorMapLine__label--anchor`,
};

const Styles = {
	[LineCSS.container]: {
		pointerEvents: 'none',
		transform: 'translate3d(0, 0, -1px)',

		[`&:hover .${LineCSS.highlight}`]: {
			opacity: 1,
		},
	},
	[LineCSS.clickTarget]: {
		cursor: 'pointer',
		pointerEvents: 'all',
	},
	[LineCSS.highlight]: {
		opacity: 0,
		transition: 'opacity 150ms ease',
		transform: 'translate3d(0, 0, -1px)',
	},
	[`${LineCSS.lineSelected} .${LineCSS.highlight}`]: {
		opacity: 1,
	},
	[`${LineCSS.lineSelected} .${LineCSS.label}`]: {
		opacity: 1,
	},
	[LineCSS.label]: {
		cursor: 'pointer',
		position: 'absolute',
		transform: 'translate3d(-50%, -100%, -1px)',
		opacity: 0.75,
	},
	[LineCSS.labelText]: {
		padding: '2px 4px',
		cursor: 'pointer',
		pointerEvents: 'all',
		transformOrigin: '50% 50%',
		color: rgba(BLACK),
		background: getSpanLineColor('unknown'),
		transition: 'background 150ms ease',
	},
	[LineCSS.handle]: {
		cursor: 'crosshair',
		userSelect: 'none',
		transform: 'translate3d(0, 0, -1px)',
		pointerEvents: 'all',
	},
	[`${LineCSS.dragging} .${LineCSS.handle}`]: {
		cursor: 'move',
	},
	[LineCSS.labelSpan]: {
		background: getSpanLineColor('span'),
	},
	[LineCSS.labelDropspan]: {
		background: getSpanLineColor('dropspan'),
	},
	[LineCSS.labelVectorAnchor]: {
		background: getSpanLineColor('vector-anchor'),
	},
	[LineCSS.labelAnchor]: {
		background: getSpanLineColor('anchor'),
	},
};

addStyles(Styles);

function getSpanType(field: CollectionModelFieldVector | CollectionModelFieldAnchor): MaybeSpanType {
	// (NEW)     If the parent subform is a span subform and this is the span vector field,
	// (LEGACY)  Or the subform has the title of "span" | "spans"
	//   Then return span related colors
	if (
		(CollectionModelFieldVector.isVectorField(field) &&
			field.schema.parent?.subFormSpanTypeState?.vectorFieldId === field.schema.id) ||
		['span', 'spans'].includes(field.parent.schema.displayName.toLowerCase())
	) {
		if (field.provider === CollectionModelFieldVector.PROVIDER_COLLECTION) {
			return 'span';
		}

		return 'dropspan';
	}

	if (field.parent.schema.displayName.toLowerCase() === 'anchor') {
		return 'vector-anchor';
	}

	if (CollectionModelFieldAnchor.isAnchorField(field)) {
		return 'anchor';
	}

	return 'unknown';
}

export function getSpanLineColor(kind: MaybeSpanType, opacity = 1): string {
	let color;

	switch (kind) {
		case 'span':
			color = ColorPaletteOrange.C400;
			break;

		case 'dropspan':
			color = ColorPaletteYellow.C400;
			break;

		case 'vector-anchor':
			color = ColorPaletteBlue.C400;
			break;

		case 'anchor':
			color = ColorPaletteBlue.A700;
			break;

		default:
			color = ColorPaletteGrey.C800;
			break;
	}

	return rgba(color, opacity);
}

export class MapLine {
	kind: MaybeSpanType;
	initialPoint: google.maps.Point;
	terminalPoint: google.maps.Point;
	width: number;
	height: number;
	length: number;
	angle: number;
	leftPx: string;

	constructor(private opts: ITopologicalEditorMapLineOptions) {
		this.kind = getSpanType(opts.vector.field);
		this.terminalPoint = PointCache.getPoint(opts.vector.target.latitude, opts.vector.target.longitude);

		const [topLeft, bottomRight] = this.boundingRect;

		this.width = Math.abs(topLeft.x - bottomRight.x);
		this.height = Math.abs(topLeft.y - bottomRight.y);
		this.length = Math.hypot(this.width, this.height);
		this.angle = Math.atan2(this.terminalPoint.y - opts.initialPoint.y, this.terminalPoint.x - opts.initialPoint.x);
		this.leftPx = `${opts.initialPoint.x}px`;
	}

	get calculatedTerminalPoint() {
		return this.opts.isSelected
			? {
					x: this.terminalPoint.x + this.opts.vectorHandleMoveDelta.x,
					y: this.terminalPoint.y + this.opts.vectorHandleMoveDelta.y,
				}
			: this.terminalPoint;
	}

	get boundingRect() {
		return [
			new google.maps.Point(
				Math.min(this.opts.initialPoint.x, this.calculatedTerminalPoint.x),
				Math.min(this.opts.initialPoint.y, this.calculatedTerminalPoint.y)
			),
			new google.maps.Point(
				Math.max(this.opts.initialPoint.x, this.calculatedTerminalPoint.x),
				Math.max(this.opts.initialPoint.y, this.calculatedTerminalPoint.y)
			),
		];
	}

	get lineDimensions() {
		return {
			x1: this.opts.initialPoint.x,
			y1: this.opts.initialPoint.y,
			x2: this.calculatedTerminalPoint.x,
			y2: this.calculatedTerminalPoint.y,
		};
	}

	get clickTarget() {
		return m('line', {
			className: LineCSS.clickTarget,
			'stroke-width': '12px',
			stroke: 'rgba(0,0,0,0)',
			role: 'button',
			onclick: this.opts.onClick,
			...this.lineDimensions,
		});
	}

	get line() {
		return m('g', [
			m('line', {
				className: LineCSS.highlight,
				stroke: rgba(WHITE),
				'stroke-width': '4px',
				'stroke-dasharray': '2px 2px',
				...this.lineDimensions,
			}),
			m('line', {
				className: LineCSS.line,
				stroke: getSpanLineColor(this.kind),
				'stroke-width': '2px',
				...this.lineDimensions,
			}),
		]);
	}

	get handle() {
		let handleEl = null;

		if (this.opts.isSelected && this.kind !== 'span') {
			handleEl = m('circle', {
				className: LineCSS.handle,
				cx: this.terminalPoint.x + this.opts.vectorHandleMoveDelta.x,
				cy: this.terminalPoint.y + this.opts.vectorHandleMoveDelta.y,
				r: 4,
				fill: rgba(WHITE),
				stroke: rgba(BLACK),
				'stroke-width': '1px',
				onmousedown: this.opts.onVectorHandleMouseDown,
			});
		}

		return handleEl;
	}

	get label() {
		if (this.opts.showVectorLabel || this.opts.drawLabels) {
			let distance = 0;
			let bearing = 0;
			if (CollectionModelFieldAnchor.isAnchorField(this.opts.vector.field)) {
				const distanceBearingPitch: DistanceBearingPitch = (<CollectionModelFieldAnchor>(
					this.opts.vector.field
				)).distanceBearingPitch(this.opts.vector.source);
				distance = distanceBearingPitch.distance;
				bearing = distanceBearingPitch.bearing;
			} else {
				const newVector = IkeGeodesic.getVector(this.opts.vector.source, this.opts.vector.target);
				distance = newVector.distance;
				bearing = newVector.bearing;
			}

			const [topLeft, bottomRight] = this.boundingRect;
			const lineAngle = Math.atan2(
				this.calculatedTerminalPoint.y - this.opts.initialPoint.y,
				this.calculatedTerminalPoint.x - this.opts.initialPoint.x
			);

			let textAngle = lineAngle;

			// Don't render upside down text
			if (textAngle > Math.PI / 2 || textAngle < -Math.PI / 2) {
				textAngle = Math.PI + lineAngle;
			}

			const midPoint = {
				x: (topLeft.x + bottomRight.x) / 2,
				y: (topLeft.y + bottomRight.y) / 2,
			};

			return m(
				'div',
				{
					className: LineCSS.label,
					style: {
						left: `${midPoint.x}px`,
						top: `${midPoint.y}px`,
					},
				},
				m(
					'div',
					{
						className: classNames(LineCSS.labelText, {
							[LineCSS.labelVectorAnchor]: this.kind === 'vector-anchor',
							[LineCSS.labelAnchor]: this.kind === 'anchor',
							[LineCSS.labelSpan]: this.kind === 'span',
							[LineCSS.labelDropspan]: this.kind === 'dropspan',
						}),
						style: {
							transform: `rotate(${textAngle}rad) translateZ(-1px)`,
						},
						onclick: this.opts.onClick,
					},
					IkeUnit.display(distance, Services.UserSettings.units) +
						` ${IkeUnit.displayAngle(bearing, IkeUnitAngle.DEGREE)}`
				)
			);
		}

		return null;
	}

	get nodes() {
		return m(
			'div',
			{
				className: classNames(LineCSS.container, {
					[LineCSS.lineSelected]: this.opts.isSelected,
				}),
				onmouseover: this.opts.onVectorMouseOver,
				onmouseout: this.opts.onVectorMouseOut,
			},
			[
				m(
					'svg',
					{
						className: LineCSS.vector,
						style: {
							overflow: 'visible',
							position: 'absolute',
						},
					},
					[this.line, this.clickTarget, this.handle]
				),
				this.label,
			]
		);
	}
}
